import { Alert, Button, Container, Pending } from '@cluey/cluey-components';
import { first, isEmpty } from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import creditCard from '../../../../../assets/images/atoms-icon-outline-credit-card.svg';
import EnrolmentLayout from '../../../../../common/hub/EnrolmentLayout';
import { Loader } from '../../../../../components/Loader';
import { useCreateOneOffSession } from '../../../../../hooks/mutations/useSessionMutation';
import useAccountPricingPlanQuery from '../../../../../hooks/queries/useAccountPricingPlanQuery';
import { useGetSoonToExpireVouchers, useGetVouchers } from '../../../../../hooks/queries/useVoucherQuery';
import { CalendarIcon, ProfileIcon } from '../../../../../icons';
import { getSpecificActiveEnrolment } from '../../../../../selectors/enrolmentSelector';
import { getAccountDetails } from '../../../../../selectors/hubSelector';
import { getOneOffSessionDiscountEligibility } from '../../../../../selectors/sessionSelector';
import {
  AccountDetails,
  Enrolment,
  EnrolmentDetail,
  FormFlowProps,
  TutorTimeSlot,
} from '../../../../../types/hubInterfaces';
import { DATE_FORMAT_TYPE_8, DATE_FORMAT_TYPE_9, SESSION_TIME_FORMAT } from '../../../../../util/constants';
import { getSessionCost } from '../../../../../util/helper/oneOffSession';
import { setTutorTypeDefault } from '../../../../../util/helpers';
import { VoucherMessage } from '../Voucher/VoucherMessage';

interface Props {
  formFlowProps: FormFlowProps;
}

interface State {
  enrolmentDetail: EnrolmentDetail;
  currentSelectedDate: string;
  timeSlot: TutorTimeSlot;
  otherTutor: boolean;
}

const Confirm = ({ formFlowProps }: Props) => {
  const history = useHistory<State>();
  const {
    location: { state },
  } = history;
  const { enrolmentDetail, currentSelectedDate, timeSlot, otherTutor } = state ?? {};
  const {
    curriculum,
    enrolmentDiscipline,
    enrolmentId,
    enrolmentSfid,
    enrolmentSubject,
    enrolmentYearLevel,
    studentName,
    tutorSfId: tutorId,
    tutorName,
    tutorPhoto,
    tutorTimezone,
    tutorDescription,
    tutorState,
    tutorType,
  } = enrolmentDetail ?? {};
  const {
    data: pricingPlanData,
    isLoading: isLoadingPricingPlan,
    isError: isErrorPricingPlan,
  } = useAccountPricingPlanQuery();

  // Extract tutor selection logic into a single object for better maintainability
  const selectedTutor =
    otherTutor && timeSlot?.tutorsAvailable?.[0]
      ? {
          name: timeSlot.tutorsAvailable[0].tutorName,
          photo: timeSlot.tutorsAvailable[0].tutorPhoto,
          state: timeSlot.tutorsAvailable[0].state,
          type: timeSlot.tutorsAvailable[0].tutorType,
          bio: timeSlot.tutorsAvailable[0].tutorBio,
          id: timeSlot.tutorsAvailable[0].tutorId,
        }
      : {
          name: tutorName,
          photo: tutorPhoto,
          state: tutorState,
          type: tutorType,
          bio: tutorDescription,
          id: tutorId,
        };

  const {
    name: selectedTutorName,
    photo: selectedTutorPhoto,
    state: selectedTutorState,
    type: selectedTutorType,
    bio: selectedTutorBio,
    id: selectedTutorId,
  } = selectedTutor;

  const {
    customerSfid: customerId,
    firstName: recipientFirstname,
    timezone: recipientTimezone,
    country: recipientCountry,
  }: AccountDetails = useSelector(getAccountDetails);
  const enrolment: Enrolment = useSelector(getSpecificActiveEnrolment({ enrolmentId }));
  const sessionCost = getSessionCost({ data: pricingPlanData });
  const { data: vouchers } = useGetVouchers('Active');
  const { data: soonToExpireVoucher } = useGetSoonToExpireVouchers();

  const discountEligible = useSelector(
    getOneOffSessionDiscountEligibility({
      enrolmentId,
      date: moment(currentSelectedDate).format('YYYY-MM-DD'),
    })
  );

  useEffect(() => {
    if (!enrolmentDetail || !currentSelectedDate || !timeSlot) {
      formFlowProps.jump(0);
    }
  }, [formFlowProps, enrolmentDetail, currentSelectedDate, timeSlot]);

  const { isError, isLoading, mutateAsync } = useCreateOneOffSession({
    data: {
      tutorId: selectedTutorId,
      enrolmentId: enrolmentSfid,
      customerId,
      recipientFirstname,
      recipientTimezone,
      recipientCountry,
      studentFirstName: enrolment?.student,
      studentName: `${enrolment?.student} ${enrolment?.lastname}`,
      studentYearLevel: enrolmentYearLevel,
      studentCurriculum: curriculum,
      programSubject: enrolmentSubject,
      sessionStartDateTime: moment.utc(timeSlot?.start).format(DATE_FORMAT_TYPE_9),
      sessionEndDateTime: moment.utc(timeSlot?.end).format(DATE_FORMAT_TYPE_9),
      tutorFirstName: selectedTutorName,
      tutorTimezone,
      tutorPhoto: selectedTutorPhoto,
      voucherId: !isEmpty(soonToExpireVoucher) ? first(soonToExpireVoucher).id.toString() : undefined,
    },
    onSuccess: () => {
      if (!isEmpty(soonToExpireVoucher)) {
        formFlowProps.jump(3, {
          enrolmentDetails: enrolmentDetail,
          vouchers,
          timeSlot,
          tutorName: selectedTutorName,
          timezone: recipientTimezone,
        });
      } else {
        formFlowProps.next({ enrolmentDetail, discountEligible });
      }
    },
  });

  const confirmAddSession = async () => {
    await mutateAsync();
  };

  const getSessionCostText = () => {
    return `We will attempt to charge $${sessionCost || ''} for this session immediately.`;
  };

  return (
    <EnrolmentLayout
      sectionHeading={`${studentName} ${enrolmentDiscipline}`}
      mainHeading="Confirm session details"
      enrolmentId={enrolmentId}
      showEnrolmentStatus={false}
      showSummerModeEnrolmentStatus={false}
      isLoading={isLoadingPricingPlan}
    >
      <Container>
        {isErrorPricingPlan && (
          <Alert
            className="mb-6"
            type="error"
            body="There was an error retrieving your pricing plan. You won't be able to see an accurate session cost."
          />
        )}
        <p className="text-grey-5">Please review and confirm the new session time</p>
        <section className="mt-6 flex flex-col justify-between border border-grey-2 md:flex-row">
          <div className="flex w-3/5 flex-col gap-y-4 p-8 md:p-16">
            <p className="rounded text-xs font-bold uppercase text-grey-5">NEW SESSION</p>
            <div className="flex gap-x-3 text-grey-5">
              <CalendarIcon width={24} height={24} className="fill-blood-orange-4" />
              <div className="text-lg">
                <div>{moment.utc(timeSlot?.start).tz(recipientTimezone).format(DATE_FORMAT_TYPE_8)}</div>
                <div>
                  {`
                    ${moment.utc(timeSlot?.start).tz(recipientTimezone).format(SESSION_TIME_FORMAT)}
                     - 
                    ${moment.utc(timeSlot?.end).tz(recipientTimezone).format(SESSION_TIME_FORMAT)} 
                  `}
                </div>
              </div>
            </div>
            {otherTutor && (
              <div className="flex gap-x-3">
                <ProfileIcon width={24} height={24} className="fill-blood-orange-4" />
                <div className="text-lg">
                  <p>
                    Your tutor for the session will be <b>{selectedTutorName}</b>
                  </p>
                </div>
              </div>
            )}

            {soonToExpireVoucher && soonToExpireVoucher.length > 0 ? (
              <VoucherMessage
                className="!mb-4"
                body={
                  <>
                    <p>We'll automatically use the Cluey Voucher that expires soonest to book this make-up session.</p>
                    <p>
                      You'll have {vouchers.length - 1} voucher{vouchers.length - 1 > 1 ? 's' : ''} remaining after this
                      booking is confirmed.
                    </p>
                  </>
                }
              />
            ) : (
              <div className="flex gap-x-3 text-grey-5">
                <img className="self-start" src={creditCard} alt="credit-card" width={22} />
                <div className="text-sm">
                  <div>{getSessionCostText()}</div>
                </div>
              </div>
            )}

            <div className="mt-2.5">
              <Pending loading={isLoading} loader={<Loader />}>
                <Button className="w-full md:w-auto" onClick={confirmAddSession} size="lg">
                  Confirm session
                </Button>
              </Pending>
              {isError && (
                <div className="mt-2.5 text-sm text-red-5">
                  There was a problem encountered when trying to create your session on the chosen date.
                </div>
              )}
            </div>
          </div>
          <div className="w-2/5 bg-slate-1">
            <div className="flex flex-col gap-x-4 p-8 md:p-16">
              <div className="flex flex-row gap-x-5 border-b border-grey-2 pb-7">
                <div className="w-16 overflow-hidden rounded-full">
                  <img src={selectedTutorPhoto} alt={`${selectedTutorName}`} />
                </div>
                <div className="my-auto text-grey-5">
                  <div className="text-lg font-bold">{selectedTutorName}</div>
                  <div className="flex flex-row gap-x-1">
                    {tutorState && <span className="text-grey-5">{selectedTutorState}</span>}
                    {setTutorTypeDefault({ tutorType: selectedTutorType })}
                  </div>
                </div>
              </div>
              {selectedTutorBio && (
                <div className="flex flex-col gap-y-4 py-8 text-sm">
                  <p className="font-bold uppercase">A litte about {selectedTutorName}</p>
                  {selectedTutorBio}
                </div>
              )}
            </div>
          </div>
        </section>
      </Container>
    </EnrolmentLayout>
  );
};

export { Confirm };
